import React, { useEffect, useState } from 'react';
import { 
  Card, 
  Row, 
  Col, 
  Typography, 
  Skeleton, 
  Select, 
  Switch, 
  Slider, 
  InputNumber, 
  Button, 
  message, 
  Space,
  Divider,
  Tooltip
} from 'antd';
import {
  PlayCircleOutlined,
  VideoCameraOutlined,
  MessageOutlined,
  StarOutlined,
  SaveOutlined,
  ReloadOutlined,
  InfoCircleOutlined,
} from '@ant-design/icons';
import { useAuth } from '../contexts/AuthContext';
import { 
  WaitingConfigService, 
  WaitingConfig, 
  WaitingType, 
  WAITING_TYPES,
  DEFAULT_CONFIG 
} from '../services/waitingConfigService';
import { getActiveBrandId } from '../config/api';

const { Title, Text, Paragraph } = Typography;

const WaitingConfigPage: React.FC = () => {
  const { user } = useAuth();
  const [config, setConfig] = useState<WaitingConfig>(DEFAULT_CONFIG);
  const [originalConfig, setOriginalConfig] = useState<WaitingConfig>(DEFAULT_CONFIG);
  const [configId, setConfigId] = useState<string | null>(null);
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [waitingType, setWaitingType] = useState<WaitingType>('fashplay');
  const [hasChanges, setHasChanges] = useState(false);

  const activeBrandId = getActiveBrandId(user);

  useEffect(() => {
    loadConfig();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [activeBrandId, waitingType]);

  useEffect(() => {
    const configChanged = JSON.stringify(config) !== JSON.stringify(originalConfig);
    setHasChanges(configChanged);
  }, [config, originalConfig]);

  const sanitizeConfig = (rawConfig: any): WaitingConfig => {
    return {
      layout: rawConfig?.layout || DEFAULT_CONFIG.layout,
      soft_gate_seconds: rawConfig?.soft_gate_seconds ?? DEFAULT_CONFIG.soft_gate_seconds,
      estimated_duration_seconds: rawConfig?.estimated_duration_seconds ?? DEFAULT_CONFIG.estimated_duration_seconds,
      modules: {
        reels: rawConfig?.modules?.reels ?? DEFAULT_CONFIG.modules.reels,
        brand_reels: rawConfig?.modules?.brand_reels ?? DEFAULT_CONFIG.modules.brand_reels,
        chat_offer: rawConfig?.modules?.chat_offer ?? DEFAULT_CONFIG.modules.chat_offer,
        highlight: rawConfig?.modules?.highlight ?? DEFAULT_CONFIG.modules.highlight,
      },
      triggers: {
        highlight_pct: rawConfig?.triggers?.highlight_pct ?? DEFAULT_CONFIG.triggers.highlight_pct,
        chat_pct: rawConfig?.triggers?.chat_pct ?? DEFAULT_CONFIG.triggers.chat_pct,
      },
    };
  };

  const loadConfig = async () => {
    if (!activeBrandId) {
      setLoading(false);
      return;
    }

    try {
      setLoading(true);
      const result = await WaitingConfigService.getConfig(activeBrandId, waitingType);
      const cleanConfig = sanitizeConfig(result.config);
      setConfig(cleanConfig);
      setOriginalConfig(cleanConfig);
      setConfigId(result.configId);
    } catch (error) {
      console.error('Failed to load waiting config:', error);
      message.error('Yapılandırma yüklenemedi');
    } finally {
      setLoading(false);
    }
  };

  const handleSave = async () => {
    if (!activeBrandId) return;

    try {
      setSaving(true);
      const cleanConfigToSave = sanitizeConfig(config);
      const updatedConfig = await WaitingConfigService.updateConfig(
        activeBrandId,
        waitingType,
        cleanConfigToSave
      );
      const cleanUpdatedConfig = sanitizeConfig(updatedConfig);
      setConfig(cleanUpdatedConfig);
      setOriginalConfig(cleanUpdatedConfig);
      message.success('Yapılandırma kaydedildi');
    } catch (error) {
      console.error('Failed to save waiting config:', error);
      message.error('Yapılandırma kaydedilemedi');
    } finally {
      setSaving(false);
    }
  };

  const handleReset = () => {
    setConfig(originalConfig);
    message.info('Değişiklikler geri alındı');
  };

  const updateModule = (module: keyof WaitingConfig['modules'], value: boolean) => {
    setConfig(prev => ({
      ...prev,
      modules: {
        ...prev.modules,
        [module]: value
      }
    }));
  };

  const updateTrigger = (trigger: keyof WaitingConfig['triggers'], value: number) => {
    setConfig(prev => ({
      ...prev,
      triggers: {
        ...prev.triggers,
        [trigger]: value
      }
    }));
  };

  const moduleItems = [
    {
      key: 'reels',
      label: 'Kullanıcı Reels',
      description: 'Kullanıcıların oluşturduğu FashPlay videolarını göster',
      icon: <PlayCircleOutlined style={{ fontSize: 20, color: '#667eea' }} />,
    },
    {
      key: 'brand_reels',
      label: 'Marka Reels',
      description: 'Markanız için oluşturulan showcase videolarını göster',
      icon: <VideoCameraOutlined style={{ fontSize: 20, color: '#722ed1' }} />,
    },
    {
      key: 'highlight',
      label: 'Highlight Kartı',
      description: 'Özel vurgu içerikleri göster',
      icon: <StarOutlined style={{ fontSize: 20, color: '#faad14' }} />,
      trigger: 'highlight_pct',
    },
    {
      key: 'chat_offer',
      label: 'Chat Teklifi',
      description: 'Canlı destek teklifini göster',
      icon: <MessageOutlined style={{ fontSize: 20, color: '#52c41a' }} />,
      trigger: 'chat_pct',
    },
  ];

  if (loading) {
    return (
      <div style={{ padding: 24 }}>
        <Skeleton active paragraph={{ rows: 1 }} style={{ marginBottom: 24 }} />
        <Row gutter={[16, 16]}>
          {[1, 2].map((i) => (
            <Col xs={24} lg={12} key={i}>
              <Card><Skeleton active paragraph={{ rows: 6 }} /></Card>
            </Col>
          ))}
        </Row>
      </div>
    );
  }

  return (
    <div>
      <div style={{ marginBottom: 24, display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start', flexWrap: 'wrap', gap: 16 }}>
        <div>
          <Title level={2} style={{ marginBottom: 4 }}>Bekleme Ekranı Yapılandırması</Title>
          <Text type="secondary">Modülleri ve tetikleyici ayarlarını yönetin</Text>
        </div>
        
        <div style={{ display: 'flex', gap: 12, flexWrap: 'wrap', alignItems: 'center' }}>
          <Select
            value={waitingType}
            onChange={(value) => setWaitingType(value)}
            style={{ width: 160 }}
            options={WAITING_TYPES}
          />
          
          <Space>
            <Button 
              icon={<ReloadOutlined />} 
              onClick={handleReset}
              disabled={!hasChanges}
            >
              Geri Al
            </Button>
            <Button 
              type="primary" 
              icon={<SaveOutlined />} 
              onClick={handleSave}
              loading={saving}
              disabled={!hasChanges}
            >
              Kaydet
            </Button>
          </Space>
        </div>
      </div>

      <Row gutter={[16, 16]}>
        <Col xs={24} lg={12}>
          <Card 
            title={
              <div style={{ display: 'flex', alignItems: 'center', gap: 8 }}>
                <span>⚙️</span>
                <span>Genel Ayarlar</span>
              </div>
            }
            style={{ borderRadius: 16, height: '100%' }}
          >
            <div style={{ marginBottom: 24 }}>
              <div style={{ display: 'flex', alignItems: 'center', gap: 8, marginBottom: 8 }}>
                <Text strong>Soft Gate Süresi</Text>
                <Tooltip title="Sonuç hazır olduktan sonra kullanıcının bekletileceği ek süre (saniye)">
                  <InfoCircleOutlined style={{ color: '#8c8c8c' }} />
                </Tooltip>
              </div>
              <div style={{ display: 'flex', alignItems: 'center', gap: 16 }}>
                <Slider
                  min={0}
                  max={30}
                  value={config.soft_gate_seconds}
                  onChange={(value) => setConfig(prev => ({ ...prev, soft_gate_seconds: value }))}
                  style={{ flex: 1 }}
                />
                <InputNumber
                  min={0}
                  max={30}
                  value={config.soft_gate_seconds}
                  onChange={(value) => setConfig(prev => ({ ...prev, soft_gate_seconds: value || 0 }))}
                  style={{ width: 70 }}
                  suffix="sn"
                />
              </div>
              <Text type="secondary" style={{ fontSize: 12 }}>
                Kullanıcıyı sonuç hazır olduktan sonra {config.soft_gate_seconds} saniye daha beklet
              </Text>
            </div>

            <div>
              <div style={{ display: 'flex', alignItems: 'center', gap: 8, marginBottom: 8 }}>
                <Text strong>Tahmini Süre</Text>
                <Tooltip title="İşlemin tahmini tamamlanma süresi (ilerleme çubuğu için)">
                  <InfoCircleOutlined style={{ color: '#8c8c8c' }} />
                </Tooltip>
              </div>
              <div style={{ display: 'flex', alignItems: 'center', gap: 16 }}>
                <Slider
                  min={10}
                  max={120}
                  value={config.estimated_duration_seconds}
                  onChange={(value) => setConfig(prev => ({ ...prev, estimated_duration_seconds: value }))}
                  style={{ flex: 1 }}
                />
                <InputNumber
                  min={10}
                  max={120}
                  value={config.estimated_duration_seconds}
                  onChange={(value) => setConfig(prev => ({ ...prev, estimated_duration_seconds: value || 30 }))}
                  style={{ width: 70 }}
                  suffix="sn"
                />
              </div>
              <Text type="secondary" style={{ fontSize: 12 }}>
                İlerleme çubuğu bu süreye göre ilerleyecek
              </Text>
            </div>
          </Card>
        </Col>

        <Col xs={24} lg={12}>
          <Card 
            title={
              <div style={{ display: 'flex', alignItems: 'center', gap: 8 }}>
                <span>📦</span>
                <span>Modüller</span>
              </div>
            }
            style={{ borderRadius: 16, height: '100%' }}
          >
            <Paragraph type="secondary" style={{ marginBottom: 16 }}>
              Bekleme ekranında gösterilecek içerik modüllerini aktif/pasif yapın
            </Paragraph>

            {moduleItems.map((item, index) => (
              <div key={item.key}>
                <div style={{ 
                  display: 'flex', 
                  alignItems: 'center', 
                  justifyContent: 'space-between',
                  padding: '12px 0'
                }}>
                  <div style={{ display: 'flex', alignItems: 'center', gap: 12 }}>
                    <div style={{
                      width: 40,
                      height: 40,
                      borderRadius: 10,
                      background: '#f5f5f5',
                      display: 'flex',
                      alignItems: 'center',
                      justifyContent: 'center'
                    }}>
                      {item.icon}
                    </div>
                    <div>
                      <Text strong>{item.label}</Text>
                      <div>
                        <Text type="secondary" style={{ fontSize: 12 }}>
                          {item.description}
                        </Text>
                      </div>
                    </div>
                  </div>
                  <Switch
                    checked={config.modules?.[item.key as keyof WaitingConfig['modules']] ?? false}
                    onChange={(checked) => updateModule(item.key as keyof WaitingConfig['modules'], checked)}
                  />
                </div>
                {index < moduleItems.length - 1 && <Divider style={{ margin: 0 }} />}
              </div>
            ))}
          </Card>
        </Col>
      </Row>

      <Card 
        title={
          <div style={{ display: 'flex', alignItems: 'center', gap: 8 }}>
            <span>🎯</span>
            <span>Tetikleyici Ayarları</span>
          </div>
        }
        style={{ borderRadius: 16, marginTop: 16 }}
      >
        <Paragraph type="secondary" style={{ marginBottom: 24 }}>
          Her modülün hangi ilerleme yüzdesinde gösterileceğini ayarlayın. Örneğin, %30'da anket kartı görünür.
        </Paragraph>

        <Row gutter={[24, 24]}>
          {moduleItems.filter(m => m.trigger).map((item) => (
            <Col xs={24} sm={12} lg={6} key={item.key}>
              <Card 
                size="small" 
                style={{ 
                  borderRadius: 12,
                  opacity: config.modules?.[item.key as keyof WaitingConfig['modules']] ? 1 : 0.5
                }}
              >
                <div style={{ display: 'flex', alignItems: 'center', gap: 8, marginBottom: 12 }}>
                  {item.icon}
                  <Text strong style={{ fontSize: 13 }}>{item.label}</Text>
                </div>
                
                <div style={{ marginBottom: 8 }}>
                  <Slider
                    min={0}
                    max={100}
                    value={config.triggers?.[item.trigger as keyof WaitingConfig['triggers']] ?? 0}
                    onChange={(value) => updateTrigger(item.trigger as keyof WaitingConfig['triggers'], value)}
                    disabled={!config.modules?.[item.key as keyof WaitingConfig['modules']]}
                    marks={{
                      0: '0%',
                      50: '50%',
                      100: '100%'
                    }}
                  />
                </div>
                
                <div style={{ textAlign: 'center' }}>
                  <Text type="secondary" style={{ fontSize: 12 }}>
                    İlerleme %{config.triggers?.[item.trigger as keyof WaitingConfig['triggers']] ?? 0} olunca göster
                  </Text>
                </div>
              </Card>
            </Col>
          ))}
        </Row>
      </Card>

      <Card 
        title={
          <div style={{ display: 'flex', alignItems: 'center', gap: 8 }}>
            <span>📋</span>
            <span>Yapılandırma Özeti</span>
          </div>
        }
        style={{ borderRadius: 16, marginTop: 16 }}
      >
        <Row gutter={[16, 16]}>
          <Col xs={24} md={8}>
            <div style={{ padding: 16, background: '#f5f5f5', borderRadius: 12 }}>
              <Text type="secondary" style={{ fontSize: 12 }}>Aktif Modüller</Text>
              <Title level={3} style={{ margin: '4px 0' }}>
                {Object.values(config.modules || {}).filter(Boolean).length} / {Object.keys(config.modules || {}).length}
              </Title>
            </div>
          </Col>
          <Col xs={24} md={8}>
            <div style={{ padding: 16, background: '#f5f5f5', borderRadius: 12 }}>
              <Text type="secondary" style={{ fontSize: 12 }}>Soft Gate Süresi</Text>
              <Title level={3} style={{ margin: '4px 0' }}>
                {config.soft_gate_seconds} saniye
              </Title>
            </div>
          </Col>
          <Col xs={24} md={8}>
            <div style={{ padding: 16, background: '#f5f5f5', borderRadius: 12 }}>
              <Text type="secondary" style={{ fontSize: 12 }}>Tahmini Süre</Text>
              <Title level={3} style={{ margin: '4px 0' }}>
                {config.estimated_duration_seconds} saniye
              </Title>
            </div>
          </Col>
        </Row>
        
        {configId && (
          <div style={{ marginTop: 16, padding: '8px 16px', background: '#e6f7ff', borderRadius: 8 }}>
            <Text type="secondary" style={{ fontSize: 12 }}>
              Yapılandırma ID: <code>{configId}</code>
            </Text>
          </div>
        )}
      </Card>
    </div>
  );
};

export default WaitingConfigPage;
